import React from "react";
import { DetailedModelMetrics } from "../../types";
import { InfoTooltip } from "../common/InfoTooltip.tsx";
import "./ModelMetricsTable.tsx";

interface ModelMetricsTableProps {
  modelData: DetailedModelMetrics | null;
}

// Helper to format number to fixed decimal places
const formatMetric = (value: number | undefined) => {
  return value !== undefined ? value.toFixed(4) : "N/A";
};

// Tooltip texts for table headers
const tableMetricExplanations = {
  "R²": "Bestimmtheitsmaß (R²) auf den Trainingsdaten (0-1, höher ist besser).",
  MSE: "Mean Squared Error (Mittlerer quadratischer Fehler) auf Trainingsdaten (kleiner ist besser).",
  MAE: "Mean Absolute Error (Mittlerer absoluter Fehler) auf Trainingsdaten (kleiner ist besser).",
  "CV Mean R²":
    "Mittleres R² aus der Kreuzvalidierung (Generalisierungsfähigkeit, 0-1, höher ist besser).",
  "CV Std R²":
    "Standardabweichung des R² aus der Kreuzvalidierung (Stabilität der Performance, kleiner ist besser).",
};

export const ModelMetricsTable: React.FC<ModelMetricsTableProps> = ({
  modelData,
}) => {
  if (!modelData || !modelData.metrics_per_target) {
    return (
      <div className="metrics-table-container card">
        <h3>Modell Metriken pro Zielvariable</h3>
        <p>
          Keine Metriken im Modell gefunden (erwartet unter
          `metrics_per_target`).
        </p>
      </div>
    );
  }

  const metrics = modelData.metrics_per_target;
  const targetNames = Object.keys(metrics);

  if (targetNames.length === 0) {
    return (
      <div className="metrics-table-container card">
        <h3>Modell Metriken pro Zielvariable</h3>
        <p>Keine Zielvariablen-Metriken vorhanden.</p>
      </div>
    );
  }

  return (
    <div className="metrics-table-container card">
      <h3>
        Modell Metriken pro Zielvariable
        <InfoTooltip text="Zeigt detaillierte Leistungskennzahlen des Modells für jede einzelne Zielvariable." />
      </h3>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Zielvariable</th>
              <th>
                R² <InfoTooltip text={tableMetricExplanations["R²"]} />
              </th>
              <th>
                MSE <InfoTooltip text={tableMetricExplanations["MSE"]} />
              </th>
              <th>
                MAE <InfoTooltip text={tableMetricExplanations["MAE"]} />
              </th>
              <th>
                CV Mean R²{" "}
                <InfoTooltip text={tableMetricExplanations["CV Mean R²"]} />
              </th>
              <th>
                CV Std R²{" "}
                <InfoTooltip text={tableMetricExplanations["CV Std R²"]} />
              </th>
              {/* Optionally add CV Scores column, might be wide */}
              {/* <th>CV Scores R²</th> */}
            </tr>
          </thead>
          <tbody>
            {targetNames.map((target) => {
              const targetMetrics = metrics[target];
              return (
                <tr key={target}>
                  <td>{target}</td>
                  <td>{formatMetric(targetMetrics.r2)}</td>
                  <td>{formatMetric(targetMetrics.mse)}</td>
                  <td>{formatMetric(targetMetrics.mae)}</td>
                  <td>{formatMetric(targetMetrics.cv_mean)}</td>
                  <td>{formatMetric(targetMetrics.cv_std)}</td>
                  {/* <td>{targetMetrics.cv_scores?.map(s => formatMetric(s)).join(', ') ?? 'N/A'}</td> */}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
